import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { strings } from '@/lib/i18n';
import logoGu from '@/assets/logo-gu.png';
import logoHi from '@/assets/logo-hi.png';

type Language = 'gu' | 'hi';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  strings: typeof strings.gu;
  logo: string;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('gu');
  const [loading, setLoading] = useState(true);

  // Load user's preferred language from database or localStorage
  useEffect(() => {
    loadUserLanguage();
  }, []);

  // Update document title when language changes
  useEffect(() => {
    document.title = language === 'hi' 
      ? 'दम्यन्ती बाग - मोटाभाई का वर्ग | Smart Farmer LMS'
      : 'દમયંતી બાગ - મોટાભાઈ નો વર્ગ | Smart Farmer LMS';
  }, [language]);

  const loadUserLanguage = async () => {
    try {
      // First check localStorage for quick load (with safety check)
      try {
        const storedLang = localStorage.getItem('preferred_language');
        if (storedLang === 'hi' || storedLang === 'gu') {
          setLanguageState(storedLang);
        }
      } catch (storageError) {
        // localStorage unavailable (e.g., in restricted iframe), skip silently
      }

      // Then check database if user is logged in (with SecurityError handling)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferred_language')
            .eq('auth_user_id', user.id)
            .maybeSingle();
          
          if (profile?.preferred_language) {
            const dbLang = profile.preferred_language as Language;
            setLanguageState(dbLang);
            try {
              localStorage.setItem('preferred_language', dbLang);
            } catch (storageError) {
              // localStorage unavailable, skip silently
            }
          }
        }
      } catch (authError: any) {
        // Handle SecurityError silently (occurs in restricted iframe contexts)
        if (authError?.name !== 'SecurityError' && !authError?.message?.includes('LockManager')) {
          console.error('Error loading language preference:', authError);
        }
      }
    } catch (error) {
      // Only log unexpected errors
      console.error('Error loading language preference:', error);
    } finally {
      setLoading(false);
    }
  };

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    
    // Save to localStorage (with safety check)
    try {
      localStorage.setItem('preferred_language', lang);
    } catch (storageError) {
      // localStorage unavailable, skip silently
    }
    
    // Save to database if user is logged in (with SecurityError handling)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ preferred_language: lang })
          .eq('auth_user_id', user.id);
      }
    } catch (error: any) {
      // Handle SecurityError silently (occurs in restricted iframe contexts)
      if (error?.name !== 'SecurityError' && !error?.message?.includes('LockManager')) {
        console.error('Error saving language preference:', error);
      }
    }
  };

  // Select logo based on language
  const logo = language === 'hi' ? logoHi : logoGu;
  
  // Get strings for current language
  const currentStrings = language === 'hi' ? strings.hi : strings.gu;

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      strings: currentStrings, 
      logo, 
      loading 
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
